import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema } from '../schemas/resetPasswordSchema';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPasswordPhone } from '../api/auth';
import LabeledTextField from '../components/LabeledTextField';
import UserPageLayout from '../components/UserPageLayout';
import { Box, Button, Alert, Stack } from '@mui/material';

const ResetPasswordPhonePage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const verifyToken = location.state?.verifyToken || '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const savedPhone = localStorage.getItem('resetPhone');
    // SMS認証を経ていない場合（トークンなし）は最初からやり直し
    if (!savedPhone || !verifyToken) {
      navigate('/forgot-password');
    } else {
      setPhoneNumber(savedPhone);
    }
  }, [navigate, verifyToken]);

  const onSubmit = async (data) => {
    setFormError('root', { message: '' });
    setMessage('');

    try {
      const res = await resetPasswordPhone(
        phoneNumber,
        data.password,
        data.confirmPassword,
        verifyToken
      );

      if (res.message) {
        setMessage(res.message);
        localStorage.removeItem('resetPhone');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setFormError('root', {
          type: 'manual',
          message: 'パスワードリセットに失敗しました',
        });
      }
    } catch (e) {
      setFormError('root', {
        type: 'manual',
        message: e.response?.data?.message || '通信エラーが発生しました',
      });
    }
  };

  return (
    <UserPageLayout title="パスワード再設定" maxWidth="sm">
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          maxWidth: 'none',
          boxShadow: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '15dvh',
          alignItems: 'flex-start',
        }}
      >
        <LabeledTextField
          label="新しいパスワード"
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          autoFocus
          placeholder="新しいパスワードを入力してください（8文字以上）"
          register={register}
          error={errors.password}
        />

        <LabeledTextField
          label="パスワード確認"
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          placeholder="パスワードを再入力してください"
          register={register}
          error={errors.confirmPassword}
        />

        {errors.root && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.root.message}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}

        <Stack direction="column" spacing={2} sx={{ mt: 3, width: '100%' }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ borderRadius: 5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? '処理中...' : 'パスワードをリセット'}
          </Button>
        </Stack>
      </Box>
    </UserPageLayout>
  );
};

export default ResetPasswordPhonePage;
